import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { PageLoader } from '@/components/ui/page-loader';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { BASE_URL } from '@/config/constants';
import { toast } from 'sonner';
import { FileArchive, Upload, Download, Trash2, RefreshCw, Eye, EyeOff } from 'lucide-react';
import {
  getInfoKit,
  updateInfoKit,
  uploadInfoKit,
  deleteInfoKit,
  type InfoKitFile,
} from '@/services/infoKitApi';

interface InfoKitManagerProps {
  domain: string;
}

const MAX_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB
const MAX_HEADING_LENGTH = 80;
const ZIP_MIME_TYPES = ['application/zip', 'application/x-zip-compressed'];

const DEFAULT_DOC: InfoKitFile = {
  heading: 'Press Kit',
  fileUrl: null,
  originalFileName: '',
  fileSizeBytes: 0,
  entryCount: 0,
  downloadCount: 0,
  isActive: true,
};

const formatFileSize = (bytes: number) => {
  if (!bytes) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
};

const InfoKitManager: React.FC<InfoKitManagerProps> = ({ domain }) => {
  const [doc, setDoc] = useState<InfoKitFile>(DEFAULT_DOC);
  const [heading, setHeading] = useState(DEFAULT_DOC.heading);
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragActive, setIsDragActive] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchInfoKit = () => {
    setLoading(true);
    getInfoKit()
      .then((data) => {
        const next = { ...DEFAULT_DOC, ...data };
        setDoc(next);
        setHeading(next.heading);
        setIsActive(next.isActive);
      })
      .catch(() => {
        setDoc(DEFAULT_DOC);
        setHeading(DEFAULT_DOC.heading);
        setIsActive(true);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (domain) fetchInfoKit();
  }, [domain]);

  const hasChanges = () => heading.trim() !== doc.heading || isActive !== doc.isActive;

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = heading.trim().slice(0, MAX_HEADING_LENGTH);
    if (!trimmed) {
      toast.error('Section heading cannot be empty');
      return;
    }
    setSaving(true);
    try {
      const updated = await updateInfoKit({ heading: trimmed, isActive });
      const next = { ...DEFAULT_DOC, ...updated };
      setDoc(next);
      setHeading(next.heading);
      setIsActive(next.isActive);
      toast.success('Info Kit settings saved');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleResetSettings = () => {
    setHeading(doc.heading);
    setIsActive(doc.isActive);
  };

  const handleUpload = async (file: File) => {
    const nameLooksZip = file.name.toLowerCase().endsWith('.zip');
    const typeLooksZip = ZIP_MIME_TYPES.includes(file.type) || file.type === '';
    if (!nameLooksZip && !typeLooksZip) {
      toast.error('Only .zip files are allowed');
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      toast.error('File size must be 5MB or less');
      return;
    }

    // Force the canonical MIME so the server's type gate is deterministic
    // regardless of what the OS reports. Deep archive validation happens
    // server-side regardless (magic bytes, entry inspection, malware scan).
    const normalized = new File([file], file.name, { type: 'application/zip' });

    setUploading(true);
    setUploadProgress(0);
    try {
      const updated = await uploadInfoKit(normalized, setUploadProgress);
      const next = { ...DEFAULT_DOC, ...updated };
      setDoc(next);
      setHeading(next.heading);
      setIsActive(next.isActive);
      toast.success('Info Kit uploaded — it passed all security checks');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Upload rejected — please check the file and try again');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleUpload(file);
  };

  const handleRemove = async () => {
    setRemoving(true);
    try {
      await deleteInfoKit();
      setDoc({ ...DEFAULT_DOC, heading: doc.heading, isActive: doc.isActive });
      toast.success('Info Kit removed from your website');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to remove the info kit');
    } finally {
      setRemoving(false);
      setRemoveOpen(false);
    }
  };

  const downloadUrl = `${BASE_URL}/api/public/infokit/${domain}/download`;

  return (
    <div>
      <div className="mb-4">
        <h2 className="page-title">Info Kit</h2>
        {/* <p className="text-sm text-muted-foreground mt-1">
          One downloadable ZIP bundle (press kit, tech rider, tech details…) shown on your website.
          Maximum 5 MB.
        </p> */}
      </div>

      {loading ? (
        <PageLoader label="Loading info kit..." />
      ) : (
        <form onSubmit={handleSaveSettings} className="bg-card rounded-lg border border-border p-4 sm:p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left column — section settings */}
            <div className="space-y-5">
              <div className="form-group">
                <Label className="form-label">
                  Section Heading<span className="text-red-500">*</span>
                </Label>
                <Input
                  name="heading"
                  placeholder="Press Kit"
                  value={heading}
                  maxLength={MAX_HEADING_LENGTH}
                  onChange={(e) => setHeading(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Shown as the section title and the website navigation label. Defaults to “Press Kit”.
                </p>
              </div>

              <div className="form-group flex items-center justify-between gap-4">
                <div>
                  <Label className="form-label">Show on website</Label>
                  <p className="text-xs text-muted-foreground">
                    Hide this any time without removing the file.
                  </p>
                </div>
                <Switch checked={isActive} onCheckedChange={setIsActive} />
              </div>

              <div className="flex items-center gap-3 pt-2">
                <Button type="submit" loading={saving} disabled={!hasChanges() || uploading}>
                  Save Settings
                </Button>
                <Button
                  type="button"
                  variant="tertiary"
                  onClick={handleResetSettings}
                  disabled={!hasChanges() || saving}
                >
                  Reset
                </Button>
              </div>
            </div>

            {/* Right column — file upload / current file */}
            <div className="space-y-4">
              {uploading ? (
                <div className="border border-border rounded-lg p-6 space-y-3">
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Uploading &amp; running security checks…
                  </div>
                  <Progress value={uploadProgress} />
                  <p className="text-xs text-muted-foreground">
                    The archive is scanned (structure, file types, malware signatures) before it is stored.
                  </p>
                </div>
              ) : doc.fileUrl ? (
                <div className="border border-border rounded-lg p-6 space-y-4">
                  <div className="flex items-start gap-3">
                    <FileArchive className="w-8 h-8 text-muted-foreground shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium truncate">{doc.originalFileName || 'info-kit.zip'}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatFileSize(doc.fileSizeBytes)}
                        {doc.entryCount ? ` · ${doc.entryCount} files inside` : ''}
                        {doc.updatedAt ? ` · updated ${new Date(doc.updatedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}` : ''}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {doc.downloadCount || 0} download{(doc.downloadCount || 0) === 1 ? '' : 's'}
                        {doc.lastDownloadedAt ? ` · last ${new Date(doc.lastDownloadedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}` : ''}
                      </p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                        {isActive ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                        {isActive ? 'Visible on your website' : 'Hidden from your website'}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button type="button" variant="secondary" size="sm" onClick={() => window.open(downloadUrl, '_blank', 'noopener,noreferrer')}>
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </Button>
                    <Button type="button" variant="secondary" size="sm" onClick={() => fileInputRef.current?.click()}>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Replace
                    </Button>
                    <Button type="button" variant="destructive" size="sm" onClick={() => setRemoveOpen(true)}>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Remove
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  className={`group flex min-h-[200px] w-full flex-col items-center justify-center gap-3 py-8 px-4 border-2 border-dashed rounded-lg text-sm cursor-pointer transition-colors ${isDragActive ? 'border-primary bg-muted/50' : 'border-border hover:bg-muted/50'}`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <Upload className="w-8 h-8 text-muted-foreground" />
                  <p className="font-medium">Drag &amp; drop your ZIP here, or click to browse</p>
                  <p className="text-xs text-muted-foreground text-center">
                    One ZIP archive · max 5 MB · documents, images and audio inside
                  </p>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept=".zip,application/zip,application/x-zip-compressed"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>
          </div>
        </form>
      )}

      <ConfirmDialog
        open={removeOpen}
        onOpenChange={setRemoveOpen}
        title="Remove Info Kit?"
        description="The ZIP file will be deleted and the section disappears from your website. This cannot be undone."
        confirmLabel="Remove"
        loading={removing}
        onConfirm={handleRemove}
      />
    </div>
  );
};

export default InfoKitManager;
